import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { RotateCcw, Move, Activity, Heart, CalendarCheck } from "lucide-react";
import { Reveal } from "./Reveal";
import { WA_SCHEDULE } from "@/lib/constants";

const CARDS = [
  { icon: RotateCcw, title: "Recuperação", text: "Auxílio na recuperação da mobilidade e funcionalidade." },
  { icon: Move, title: "Movimento", text: "Melhora da capacidade de movimentação." },
  { icon: Activity, title: "Reabilitação", text: "Acompanhamento durante o processo de recuperação." },
  { icon: Heart, title: "Qualidade de vida", text: "Mais segurança e confiança para retomar suas atividades." },
];

const PHOTOS = [
  { src: "/fisio/fisio-1.jpg", alt: "Sala de fisioterapia da Clínica COB com parede O Movimento Cura" },
  { src: "/fisio/fisio-2.jpg", alt: "Espaço de exercícios e reabilitação da Clínica COB" },
  { src: "/fisio/fisio-3.jpg", alt: "Boxes de atendimento de fisioterapia da Clínica COB" },
  { src: "/fisio/fisio-4.jpg", alt: "Boxes individuais de fisioterapia da Clínica COB" },
  { src: "/fisio/fisio-5.jpg", alt: "Sala de equipamentos de fisioterapia da Clínica COB" },
];

export const Physiotherapy = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setActive((i) => (i + 1) % PHOTOS.length), 4500);
    return () => clearInterval(t);
  }, [paused]);

  return (
    <section id="fisioterapia" data-testid="physiotherapy-section" className="bg-white py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-[2.5rem] bg-cob-dark px-6 py-14 sm:px-12 lg:px-16 lg:py-20">
          <div className="pointer-events-none absolute -top-32 -right-32 h-96 w-96 rounded-full border border-cob-gold/20" />
          <div className="pointer-events-none absolute -top-16 -right-16 h-64 w-64 rounded-full border border-cob-gold/15" />
          <div className="pointer-events-none absolute -bottom-40 -left-24 h-96 w-96 rounded-full bg-cob-green/30 blur-3xl" />

          <div className="relative grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Reveal>
                <p className="text-xs font-bold uppercase tracking-[0.28em] text-cob-gold">Fisioterapia</p>
              </Reveal>
              <Reveal delay={0.08}>
                <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-[2.9rem] font-semibold tracking-tight leading-[1.1] text-white">
                  Saúde é movimento, <span className="italic text-cob-gold">Fisioterapia é vida!</span>
                </h2>
              </Reveal>
              <Reveal delay={0.16}>
                <p className="mt-5 max-w-lg text-base sm:text-lg leading-relaxed text-white/70">
                  Cuide do seu corpo, recupere seus movimentos e busque mais qualidade de vida com
                  acompanhamento profissional.
                </p>
              </Reveal>
              <Reveal delay={0.24}>
                <a
                  href={WA_SCHEDULE}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="physio-cta-whatsapp"
                  className="btn-press mt-8 inline-flex items-center gap-2.5 rounded-full bg-white px-8 py-4 text-sm font-semibold text-cob-dark transition-all duration-300 hover:bg-cob-gold hover:text-cob-dark"
                >
                  <CalendarCheck className="h-4.5 w-4.5" />
                  Agendar avaliação
                </a>
              </Reveal>
            </div>

            <Reveal delay={0.15} className="relative">
              <div
                className="relative mx-auto max-w-md lg:max-w-none"
                onMouseEnter={() => setPaused(true)}
                onMouseLeave={() => setPaused(false)}
              >
                <div className="absolute -inset-2.5 rounded-[1.8rem] border border-cob-gold/40 translate-x-3 translate-y-3 pointer-events-none" />
                <div className="relative h-[340px] sm:h-[420px] overflow-hidden rounded-[1.6rem] shadow-2xl">
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={PHOTOS[active].src}
                      src={PHOTOS[active].src}
                      alt={PHOTOS[active].alt}
                      data-testid="physio-image"
                      loading="lazy"
                      decoding="async"
                      initial={{ opacity: 0, scale: 1.05 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                  </AnimatePresence>
                </div>
                <div className="mt-4 flex justify-center gap-2.5">
                  {PHOTOS.map((photo, i) => (
                    <button
                      key={photo.src}
                      type="button"
                      data-testid={`physio-thumb-${i + 1}`}
                      aria-label={`Ver foto ${i + 1} da fisioterapia`}
                      onClick={() => setActive(i)}
                      className={`h-14 w-14 overflow-hidden rounded-xl transition-all duration-300 ${
                        i === active
                          ? "ring-2 ring-cob-gold ring-offset-2 ring-offset-cob-dark scale-105"
                          : "opacity-55 hover:opacity-90"
                      }`}
                    >
                      <img loading="lazy" decoding="async" src={photo.src} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>

          <div className="relative mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {CARDS.map((card, i) => (
              <Reveal key={card.title} delay={0.1 + i * 0.08} className="h-full">
                <div
                  data-testid={`physio-card-${i + 1}`}
                  className="h-full rounded-2xl border border-white/10 bg-white/[0.06] p-6 backdrop-blur-sm transition-colors duration-500 hover:border-cob-gold/40 hover:bg-white/[0.09]"
                >
                  <card.icon className="h-6 w-6 text-cob-gold" strokeWidth={1.7} />
                  <h3 className="mt-4 font-serif text-xl font-semibold text-white">{card.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/65">{card.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
